import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { PostingCard } from './PostingCard';
import { FeedCard } from './FeedCard';
import { ExerciseSection } from './ExerciseSection';
import { feedService } from '../services/feedService';
import { PAGE_META } from '../utils/pageTitles';
import { Post } from '../types/Post';

export function Feed() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPosts = async () => {
    try {
      const fetchedPosts = await feedService.getPosts();
      // Ensure all posts have required fields and proper field names
      const validPosts = fetchedPosts
        .filter((post): post is Post => {
          if (!post.id || !post.title || !post.content || !post.author) {
            console.warn('Skipping invalid post:', post);
            return false;
          }
          return true;
        })
        .map(post => ({
          ...post,
          // Remove any fields not in our Post type
          image_url: post.image_url,
        }));
      setPosts(validPosts);
    } catch (err) {
      setError('Failed to load posts. Please try again later.');
      console.error('Error fetching posts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const handlePostCreated = () => {
    fetchPosts();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <Helmet>
        <title>{PAGE_META.feed.title}</title>
        <meta name="description" content={PAGE_META.feed.description} />
      </Helmet>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Feed Section */}
        <div className="lg:col-span-2 space-y-6">
          {/* Create Post Section */}
          <div className="mb-8">
            <PostingCard onPostCreated={handlePostCreated} />
          </div>

          {/* Feed Section */}
          <div className="space-y-4">
            {loading ? (
              // Loading skeleton
              <div className="space-y-4">
                {[1, 2, 3].map((n) => (
                  <motion.div
                    key={n}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="bg-gray-100 dark:bg-gray-800 rounded-xl p-4 h-48 animate-pulse"
                  />
                ))}
              </div>
            ) : error ? (
              // Error state
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-xl text-center"
              >
                {error}
              </motion.div>
            ) : posts.length === 0 ? (
              // Empty state
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-12 text-gray-500 dark:text-gray-400"
              >
                <p className="text-lg">No posts yet.</p>
                <p className="text-sm mt-2">Be the first one to post!</p>
              </motion.div>
            ) : (
              // Posts list
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-4"
              >
                {posts.map((post) => (
                  <FeedCard key={post.id} post={post} />
                ))}
              </motion.div>
            )}
          </div>
        </div>

        {/* Exercise Section */}
        <div className="lg:col-span-1">
          <ExerciseSection className="sticky top-6" />
        </div>
      </div>
    </div>
  );
}